import type { FleetLiveWire } from '@developerz/domain';
import { For, type JSX, onCleanup } from 'solid-js';
import { createElementWidth } from '../lib/element-width';
import type { FleetLiveModel } from '../lib/fleet-live';
import { type Arrange, PANEL_NAME, ZONE_NAME } from '../lib/fleet-live-arrange';
import type { LiveZone } from '../lib/fleet-live-layout';
import type { LiveLoop } from '../lib/fleet-live-loop';
import type { FleetLivePanel as PanelKey } from '../lib/fleet-live-vis';
import type { ToastTone } from '../lib/toast';
import { FleetLiveCore } from './FleetLiveCore';
import { FleetLiveDeck } from './FleetLiveDeck';
import { FleetLivePanel } from './FleetLivePanel';
import { FleetLivePanelControls } from './FleetLivePanelControls';
import { FleetLiveTopology } from './FleetLiveTopology';

// Fleet Live's four layout zones and its three panels (#6779, prototype `:3522-3539`, CSS
// `:547-585`): Top, then Middle and Side in one row while both hold a panel and the section
// is at least 1,100 px wide (measured, as #6778 measures the deck), then Bottom. Each panel is
// rendered ONCE, in Top, and `createArrange` places its wrap into its zone
// (`fleet-live-zones.ts`), so a move never re-mounts it. While a grip drags, every zone shows
// its outline and label and the one under the pointer highlights.

/** Below this section width, Middle and Side stack (the prototype's 1,100 px). */
export const MID_STACK_BELOW_PX = 1100;

const MID_ROW: readonly LiveZone[] = ['middle', 'side'];

export type FleetLiveZonesProps = {
  readonly arrange: Arrange;
  readonly loop: LiveLoop;
  readonly model: FleetLiveModel;
  readonly wire: FleetLiveWire;
  readonly token: string | null;
  readonly canWrite: boolean;
  readonly notify: (message: string, tone?: ToastTone) => void;
};

export function FleetLiveZones(props: FleetLiveZonesProps): JSX.Element {
  const grid = createElementWidth();
  const a = props.arrange;
  onCleanup(() => a.unmount());
  const twoUp = (): boolean =>
    grid.width() >= MID_STACK_BELOW_PX &&
    (a.drag.active() !== null || (a.zoneHas('middle') && a.zoneHas('side')));
  const controls = (key: PanelKey): JSX.Element => (
    <FleetLivePanelControls
      panel={PANEL_NAME[key]()}
      zone={a.layout().lay[key]}
      menuOpen={a.menu() === key}
      onGrip={(e) => a.drag.press(key, e)}
      onToggleMenu={() => a.toggleMenu(key)}
      onPlace={(zone) => a.place(key, zone)}
      onReset={() => a.reset()}
      onHide={() => a.setPanel(key, false)}
      placeRef={(el) => a.placeRef(key, el)}
    />
  );
  const zone = (z: LiveZone, children?: JSX.Element): JSX.Element => (
    <div
      class="fleet-live__zone"
      classList={{
        'fleet-live__zone--empty': !a.zoneHas(z),
        'fleet-live__zone--over': a.drag.over() === z,
      }}
      data-label={ZONE_NAME[z]()}
      ref={(el) => a.zoneRef(z, el)}
    >
      {children}
    </div>
  );
  const motion = (): boolean => !props.loop.reducedMotion();
  return (
    <div
      class="fleet-live__grid"
      classList={{ 'fleet-live__grid--dragging': a.drag.active() !== null }}
      ref={(el) => grid.attach(el)}
    >
      {zone(
        'top',
        <>
          <FleetLivePanel
            title={PANEL_NAME.core()}
            shown={a.vis().core}
            motion={motion()}
            headless
            controls={null}
            wrapRef={(el) => a.wrapRef('core', el)}
            onSettled={() => a.settle()}
          >
            <FleetLiveCore
              wire={props.wire}
              loop={props.loop}
              token={props.token}
              canWrite={props.canWrite}
              notify={props.notify}
              controls={controls('core')}
            />
          </FleetLivePanel>
          <FleetLivePanel
            title={PANEL_NAME.deck()}
            shown={a.vis().deck}
            motion={motion()}
            hud={props.model.hud}
            controls={controls('deck')}
            wrapRef={(el) => a.wrapRef('deck', el)}
            onSettled={() => a.settle()}
          >
            <FleetLiveDeck model={props.model} loop={props.loop} />
          </FleetLivePanel>
          <FleetLivePanel
            title={PANEL_NAME.topo()}
            shown={a.vis().topo}
            motion={motion()}
            hud={props.model.hud}
            controls={controls('topo')}
            wrapRef={(el) => a.wrapRef('topo', el)}
            onSettled={() => a.settle()}
          >
            <FleetLiveTopology model={props.model} loop={props.loop} />
          </FleetLivePanel>
        </>,
      )}
      <div class="fleet-live__mid" classList={{ 'fleet-live__mid--two': twoUp() }}>
        <For each={MID_ROW}>{(z) => zone(z)}</For>
      </div>
      {zone('bottom')}
    </div>
  );
}
